import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Space, Typography, Modal, message, Input, Row, Col, Grid, Select, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { EditOutlined, DeleteOutlined, EyeOutlined, ExclamationCircleOutlined, SearchOutlined, QrcodeOutlined, CopyOutlined, ExportOutlined, FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons';
import apiClient from '../services/api';
import { Asset, AssetCategory, Location, Employee, Company, Supplier } from '../types';
import type { Breakpoint } from 'antd/es/_util/responsiveObserver';
import AssetDetailModal from '../components/AssetDetailModal';
import AssetCreateForm from './AssetCreateForm';
import AssetEditForm from './AssetEditForm';
import { useAuth } from '../hooks/useAuth';
import MultiAssetLabelPDF, { AssetLabelData } from '../components/MultiAssetLabelPDF';
import JsBarcode from 'jsbarcode';
// @ts-expect-error: no types for qrcode
import QRCode from 'qrcode';
import { PDFViewer, PDFDownloadLink } from '@react-pdf/renderer';
import type { SortOrder } from 'antd/es/table/interface';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import '../fonts/OpenSans-Regular-normal.js';

const { confirm } = Modal;
const { useBreakpoint } = Grid;

type AssetListResponse = { data: Asset[]; pagination: { total: number } };

const AssetListPage = () => {
  // --- States ---
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [search, setSearch] = useState('');

  const [modalVisible, setModalVisible] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [qrAsset, setQrAsset] = useState<Asset | null>(null);

  const [categories, setCategories] = useState<AssetCategory[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);

  const { user } = useAuth();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [showLabelPreview, setShowLabelPreview] = useState(false);
  const [copyAsset, setCopyAsset] = useState<Asset | null>(null);

  const [categoryFilter, setCategoryFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [locationEmployees, setLocationEmployees] = useState<{ id: number, name: string }[]>([]);

  const [barcodeBase64Map, setBarcodeBase64Map] = useState<{ [key: string]: string }>({});
  const [qrCodeBase64Map, setQrCodeBase64Map] = useState<{ [key: string]: string }>({});
  const [sorter, setSorter] = useState<{ field?: string, order?: string }>({ field: 'asset_code', order: 'descend' });

  // --- Mappings ---
  const categoryMap = useMemo(() => Object.fromEntries(categories.map(c => [c.id, c.name])), [categories]);
  const locationMap = useMemo(() => Object.fromEntries(locations.map(l => [l.id, l.name])), [locations]);
  const employeeMap = useMemo(() => Object.fromEntries(employees.map(e => [e.id, e.full_name])), [employees]);
  const companyMap = useMemo(() => Object.fromEntries(companies.map(c => [c.id, c.name])), [companies]);

  const getCategoryName = (id?: number) => id ? (categoryMap[id] || id) : '—';
  const getLocationName = (id?: number) => id ? (locationMap[id] || id) : '—';
  const getEmployeeName = (id?: number) => id ? (employeeMap[id] || id) : '—';
  const getCompanyName = (id?: number) => id ? (companyMap[id] || id) : '—';

  // --- Effects ---

  // Fetch mapping data 1 lần
  useEffect(() => {
    const fetchMappingData = async () => {
      try {
        const [cat, loc, emp, com, sup] = await Promise.all([
          apiClient.getAssetCategories(),
          apiClient.getLocations(),
          apiClient.getEmployees(),
          apiClient.getCompanies(),
          apiClient.getSuppliers(),
        ]);
        setCategories(Array.isArray(cat) ? cat : (cat && typeof cat === 'object' && 'data' in cat ? (cat as any).data : []));
        setLocations(Array.isArray(loc) ? loc : (loc && typeof loc === 'object' && 'data' in loc ? (loc as any).data : []));
        setEmployees(Array.isArray(emp) ? emp : (emp && typeof emp === 'object' && 'data' in emp ? (emp as any).data : []));
        setCompanies(Array.isArray(com) ? com : (com && typeof com === 'object' && 'data' in com ? (com as any).data : []));
        setSuppliers(Array.isArray(sup) ? sup : (sup && typeof sup === 'object' && 'data' in sup ? (sup as any).data : []));
      } catch (err) {
        console.error('Lỗi khi tải dữ liệu Mapping:', err);
      }
    };
    fetchMappingData();
  }, []);

  // Fetch assets function
  const fetchAssets = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const res = await apiClient.getAssets(page, pageSize, {
        search,
        company_id: companyFilter || undefined,
        category_id: categoryFilter || undefined,
        location_id: locationFilter || undefined,
        assigned_to_employee_id: employeeFilter || undefined,
        sort_by: sorter.field || 'asset_code',
        order: sorter.order === 'descend' ? 'desc' : 'asc',
      }) as AssetListResponse;
      setAssets(res.data);
      setPagination(prev => ({ ...prev, current: page, pageSize, total: res.pagination.total }));
    } catch (err) {
      message.error('Không thể tải danh sách tài sản');
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (user && user.role !== 'super_admin' && user.company_id) {
      setCompanyFilter(String(user.company_id));
    }

    // Set initial location filter for Asset Controller
    if (user?.role === 'employee_user' && user?.managed_location_ids && user.managed_location_ids.length > 0) {
      // If only one location, select it automatically
      if (user.managed_location_ids.length === 1) {
        setLocationFilter(String(user.managed_location_ids[0]));
      }
    }

    if (!(user && user.role !== 'super_admin' && user.company_id)) {
      fetchAssets(1, pagination.pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when filters or sorting change
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchAssets(1, pagination.pageSize);
    }, 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, companyFilter, categoryFilter, locationFilter, employeeFilter, sorter]);

  // Fetch all employees who have assets in the location (for Asset Controller filter)
  useEffect(() => {
    if (user?.role === 'employee_user' && user?.location_id) {
      const fetchLocationEmployees = async () => {
        try {
          const res = await apiClient.getAssets(1, 9999, {
            search: undefined,
            company_id: undefined,
            category_id: undefined,
            location_id: undefined,
            assigned_to_employee_id: undefined,
          }) as AssetListResponse;
          const empIds = Array.from(new Set(res.data.map(a => a.assigned_to_employee_id).filter(Boolean))) as number[];
          const empList = empIds.map(id => {
            const emp = employees.find(e => e.id === id);
            return emp ? { id: emp.id, name: emp.full_name } : null;
          }).filter(Boolean) as { id: number, name: string }[];
          setLocationEmployees(empList);
        } catch { }
      };
      if (employees.length > 0) fetchLocationEmployees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, employees]);

  // Handle Barcode & QR Code mapping
  useEffect(() => {
    const createCodesForSelectedAssets = async () => {
      const selectedAssets = assets.filter(a => selectedRowKeys.includes(a.id));
      const newBarcodeMap: { [key: string]: string } = {};
      const newQrMap: { [key: string]: string } = {};

      for (const asset of selectedAssets) {
        try {
          const canvas = document.createElement('canvas');
          JsBarcode(canvas, asset.asset_code, {
            format: 'CODE128',
            width: 2,
            height: 40,
            fontSize: 12,
            margin: 0,
            displayValue: false,
            background: 'transparent',
            lineColor: '#000',
          });
          newBarcodeMap[asset.asset_code] = canvas.toDataURL('image/png');

          const publicUrl = `${window.location.origin}/public/asset/${asset.public_uuid}`;
          const qrCode = await new Promise<string>((resolve, reject) => {
            QRCode.toDataURL(publicUrl, { width: 128, margin: 0 }, (err: Error | null, url: string) => {
              if (!err) resolve(url);
              else reject(err);
            });
          });
          newQrMap[asset.asset_code] = qrCode;
        } catch (error) {
          // Silent error
        }
      }
      setBarcodeBase64Map(newBarcodeMap);
      setQrCodeBase64Map(newQrMap);
    };

    if (selectedRowKeys.length > 0) {
      createCodesForSelectedAssets();
    }
  }, [selectedRowKeys, assets]);

  // --- Handlers ---
  const handleTableChange = (pag: any, filters: any, sorterObj: any) => {
    const newField = sorterObj.field || sorter.field;
    const newOrder = sorterObj.order || sorter.order;
    const sorterChanged = newField !== sorter.field || newOrder !== sorter.order;

    if (sorterChanged) {
      // Sorter changed → the useEffect will re-fetch at page 1
      setSorter({ field: newField, order: newOrder });
    } else {
      // Only page changed → fetch the requested page directly
      fetchAssets(pag.current, pag.pageSize);
    }
  };

  const handleDelete = (record: Asset) => {
    confirm({
      title: 'Bạn có chắc muốn xóa tài sản này?',
      icon: <ExclamationCircleOutlined />,
      content: record.name,
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      async onOk() {
        try {
          await apiClient.deleteAsset(record.id);
          message.success('Đã xóa tài sản thành công');
          fetchAssets(pagination.current, pagination.pageSize);
        } catch (err) {
          message.error('Xóa tài sản thất bại');
        }
      },
    });
  };

  const handleEdit = async (record: Asset) => {
    try {
      const assetDetail = await apiClient.getAsset(record.id);
      setEditingAsset(assetDetail);
      setEditModalOpen(true);
    } catch (err) {
      message.error('Không thể tải chi tiết tài sản');
    }
  };

  const handleCopy = async (record: Asset) => {
    try {
      const assetDetail = await apiClient.getAsset(record.id);
      setCopyAsset(assetDetail);
      setCreateModalOpen(true);
    } catch (err) {
      message.error('Không thể tải chi tiết tài sản để copy');
    }
  };

  const handleClearFilters = () => {
    setSearch('');
    setCategoryFilter('');
    setLocationFilter('');
    setEmployeeFilter('');
    if (user?.role === 'super_admin') {
      setCompanyFilter('');
    }
  };

  // --- Export Logic ---
  const getExportData = async () => {
    setExporting(true);
    try {
      const data = await apiClient.getAssetsForExport({
        search,
        company_id: companyFilter || undefined,
        category_id: categoryFilter || undefined,
        location_id: locationFilter || undefined,
        assigned_to_employee_id: employeeFilter || undefined,
        sort_by: sorter.field || 'asset_code',
        order: sorter.order === 'descend' ? 'desc' : 'asc',
      });
      return data;
    } catch (err) {
      message.error('Không thể lấy dữ liệu để xuất');
      return null;
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    const data = await getExportData();
    if (!data || data.length === 0) {
      message.info('Không có dữ liệu để xuất');
      return;
    }

    const exportData = data.map(a => ({
      'Mã tài sản': a.asset_code,
      'Tên tài sản': a.name,
      'Danh mục': getCategoryName(a.category_id),
      'Giá trị': Number(a.purchase_cost || 0),
      'Vị trí': getLocationName(a.location_id),
      'Nhân viên': getEmployeeName(a.assigned_to_employee_id),
      'Công ty': getCompanyName(a.company_id),
      'Ngày mua': a.purchase_date ? dayjs(a.purchase_date).format('DD/MM/YYYY') : '',
    }));

    const totalValue = data.reduce((sum, item) => sum + Number(item.purchase_cost || 0), 0);

    // Add summary row
    exportData.push({
      'Mã tài sản': 'TỔNG CỘNG',
      'Tên tài sản': `${data.length} tài sản`,
      'Danh mục': '',
      'Giá trị': totalValue,
      'Vị trí': '',
      'Nhân viên': '',
      'Công ty': '',
      'Ngày mua': '',
    });

    const ws = XLSX.utils.json_to_sheet(exportData);

    // Format currency for "Giá trị" column (Column D in Excel)
    // Custom format for currency with 'đ' suffix
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    for (let R = 1; R <= range.e.r; ++R) {
      const cell = ws[XLSX.utils.encode_cell({ c: 3, r: R })];
      if (cell) {
        cell.t = 'n';
        cell.z = '#,##0"đ"';
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Danh sách tài sản');
    XLSX.writeFile(wb, `Danh_sach_tai_san_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
  };

  const handleExportPdf = async () => {
    const data = await getExportData();
    if (!data || data.length === 0) {
      message.info('Không có dữ liệu để xuất');
      return;
    }

    const doc = new jsPDF('l', 'mm', 'a4');
    // Important: Register font and set as default
    doc.setFont("OpenSans-Regular");

    const formatVND = (val: number | string) => {
      const num = Number(val);
      return num.toLocaleString('en-US') + 'đ';
    };

    const tableColumn = ["Mã tài sản", "Tên tài sản", "Danh mục", "Giá trị", "Vị trí", "Nhân viên"];
    const tableRows = data.map(a => [
      a.asset_code,
      a.name,
      getCategoryName(a.category_id),
      formatVND(a.purchase_cost || 0),
      getLocationName(a.location_id),
      getEmployeeName(a.assigned_to_employee_id)
    ]);

    const totalValue = data.reduce((sum, item) => sum + Number(item.purchase_cost || 0), 0);

    doc.setFontSize(18);
    doc.text("DANH SÁCH TÀI SẢN", 14, 15);

    doc.setFontSize(11);
    doc.text(`Tổng số lượng: ${data.length} tài sản`, 14, 25);
    doc.text(`Tổng giá trị: ${formatVND(totalValue)}`, 14, 32);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 40,
      theme: 'grid',
      styles: {
        fontSize: 8,
        font: "OpenSans-Regular",
        fontStyle: 'normal'
      },
      headStyles: {
        fillColor: [22, 119, 255],
        font: "OpenSans-Regular",
        fontStyle: 'normal'
      }
    });

    doc.save(`Danh_sach_tai_san_${dayjs().format('YYYYMMDD_HHmmss')}.pdf`);
  };

  const exportMenuItems: MenuProps['items'] = [
    { key: 'excel', label: 'Xuất Excel', icon: <FileExcelOutlined />, onClick: handleExportExcel },
    { key: 'pdf', label: 'Xuất PDF', icon: <FilePdfOutlined />, onClick: handleExportPdf },
  ];

  // --- Table Columns ---
  const columns = useMemo(() => [
    {
      title: 'Mã tài sản',
      dataIndex: 'asset_code',
      key: 'asset_code',
      sorter: true,
      sortOrder: sorter.field === 'asset_code' ? (sorter.order as SortOrder) : undefined,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'] as Breakpoint[],
      width: 120,
    },
    {
      title: 'Tên tài sản',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: sorter.field === 'name' ? (sorter.order as SortOrder) : undefined,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'] as Breakpoint[],
      width: 250,
    },
    {
      title: 'Danh mục',
      dataIndex: 'category_id',
      key: 'category_id',
      render: (id: number) => getCategoryName(id),
      responsive: ['sm', 'md', 'lg', 'xl'] as Breakpoint[],
      width: 180,
    },
    {
      title: 'Vị trí',
      dataIndex: 'location_id',
      key: 'location_id',
      render: (id: number) => getLocationName(id),
      responsive: ['md', 'lg', 'xl'] as Breakpoint[],
    },
    {
      title: 'Nhân viên',
      dataIndex: 'assigned_to_employee_id',
      key: 'assigned_to_employee_id',
      render: (id: number) => getEmployeeName(id),
      responsive: ['md', 'lg', 'xl'] as Breakpoint[],
    },
    {
      title: 'Hành động',
      key: 'action',
      width: 130,
      render: (_: any, record: Asset) => (
        <Space>
          <Button icon={<EyeOutlined />} size="small" onClick={() => { setSelectedAssetId(record.id); setModalVisible(true); }} />
          {(user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'manager') && (
            <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
          )}
          {user?.role === 'super_admin' && (
            <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDelete(record)} />
          )}
          {(user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'manager') && (
            <Button size="small" icon={<CopyOutlined />} onClick={() => handleCopy(record)} style={{ background: '#e6f7ff', border: '1px solid #91d5ff' }} />
          )}
        </Space>
      ),
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'] as Breakpoint[],
    },
  ], [categoryMap, locationMap, employeeMap, companyMap, assets, user, sorter]);

  // --- Render ---
  const selectedAssets = assets.filter(a => selectedRowKeys.includes(a.id));
  const labelData: AssetLabelData[] = selectedAssets.map(a => ({
    assetCode: a.asset_code,
    assetName: a.name,
    publicUrl: `${window.location.origin}/public/asset/${a.public_uuid}`,
    companyName: companies.find(c => c.id === a.company_id)?.name || '—',
    barcodeBase64: barcodeBase64Map[a.asset_code] || null,
    qrCodeBase64: qrCodeBase64Map[a.asset_code] || null,
  }));

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16, alignItems: 'center' }}>
        <Col xs={24} md={16}>
          <Input.Search
            placeholder="Tìm kiếm theo tên hoặc mã tài sản"
            allowClear
            value={search}
            onChange={e => setSearch(e.target.value)}
            onSearch={value => setSearch(value)}
            size="large"
            style={{ width: '100%', borderRadius: 8, height: 40 }}
          />
        </Col>
        <Col xs={24} md={4}>
          <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
            <Button icon={<ExportOutlined />} size="large" style={{ borderRadius: 8, height: 40, width: '100%' }} loading={exporting}>
              Xuất danh sách
            </Button>
          </Dropdown>
        </Col>
        <Col xs={24} md={4}>
          {(user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'manager') && (
            <Button type="primary" size="large" style={{ borderRadius: 8, height: 40, width: '100%' }} onClick={() => setCreateModalOpen(true)}>
              Thêm tài sản
            </Button>
          )}
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {user?.role !== 'employee_user' && (
          <Col xs={24} md={4}>
            <Select
              value={companyFilter}
              onChange={value => {
                setCompanyFilter(value);
                setLocationFilter('');
                setEmployeeFilter('');
              }}
              placeholder="Tất cả công ty"
              allowClear
              disabled={user?.role !== 'super_admin'}
              size="large"
              style={{ width: '100%', borderRadius: 8, height: 40 }}
              options={[
                { value: '', label: 'Tất cả công ty' },
                ...companies.map(c => ({ value: String(c.id), label: c.name }))
              ]}
            />
          </Col>
        )}
        <Col xs={24} md={user?.role === 'employee_user' ? (user?.location_id ? 6 : 8) : 4}>
          <Select
            value={categoryFilter}
            onChange={value => setCategoryFilter(value)}
            placeholder="Tất cả danh mục"
            allowClear
            size="large"
            style={{ width: '100%', borderRadius: 8, height: 40 }}
            options={[
              { value: '', label: 'Tất cả danh mục' },
              ...categories.map(c => ({ value: String(c.id), label: c.name }))
            ]}
          />
        </Col>
        <Col xs={24} md={user?.role === 'employee_user' ? (user?.location_id || (user?.managed_location_ids && user.managed_location_ids.length > 0) ? 6 : 8) : 4}>
          <Select
            value={locationFilter}
            onChange={value => setLocationFilter(value)}
            placeholder="Tất cả vị trí"
            allowClear={!(user?.role === 'employee_user' && user?.managed_location_ids && user.managed_location_ids.length === 1)}
            disabled={user?.role === 'employee_user' && user?.managed_location_ids && user.managed_location_ids.length === 1}
            size="large"
            style={{ width: '100%', borderRadius: 8, height: 40 }}
            options={[
              ...(user?.role === 'employee_user' && user?.managed_location_ids && user.managed_location_ids.length === 1 ? [] : [{ value: '', label: 'Tất cả vị trí' }]),
              ...locations
                .filter(l => {
                  if (user?.role === 'employee_user' && user?.managed_location_ids && user.managed_location_ids.length > 0) {
                    return user.managed_location_ids.includes(l.id);
                  }
                  if (companyFilter) {
                    return l.company_id === Number(companyFilter);
                  }
                  return true;
                })
                .map(l => ({ value: String(l.id), label: l.name }))
            ]}
          />
        </Col>
        {(user?.role !== 'employee_user' || !!user?.location_id) && (
          <Col xs={24} md={user?.role === 'employee_user' ? 6 : 4}>
            <Select
              value={employeeFilter}
              onChange={value => setEmployeeFilter(value)}
              placeholder="Tất cả nhân viên"
              allowClear
              showSearch
              optionFilterProp="label"
              size="large"
              style={{ width: '100%', borderRadius: 8, height: 40 }}
              options={[
                { value: '', label: 'Tất cả nhân viên' },
                ...(user?.role === 'employee_user' && user?.location_id
                  ? locationEmployees.map(emp => ({ value: String(emp.id), label: emp.name }))
                  : employees
                    .filter(emp => !companyFilter || emp.company_id === Number(companyFilter))
                    .map(emp => ({ value: String(emp.id), label: emp.full_name }))
                )
              ]}
            />
          </Col>
        )}
        <Col xs={24} md={user?.role === 'employee_user' ? (user?.location_id ? 6 : 8) : 4}>
          <Button onClick={handleClearFilters} size="large" style={{ width: '100%', borderRadius: 8, height: 40 }}>Xoá lọc</Button>
        </Col>
        {user?.role !== 'employee_user' && (
          <Col xs={24} md={4}>
            {selectedRowKeys.length > 0 && (
              <Button type="primary" onClick={() => setShowLabelPreview(true)} size="large" style={{ width: '100%', borderRadius: 8, height: 40 }}>
                In tem ({selectedRowKeys.length})
              </Button>
            )}
          </Col>
        )}
      </Row>

      <Table
        rowKey="id"
        rowSelection={{
          selectedRowKeys,
          onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
        }}
        columns={columns}
        dataSource={assets}
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        onChange={handleTableChange}
        bordered
        size="middle"
      />

      {/* Modals & Preview Label */}
      <Modal
        open={showLabelPreview}
        onCancel={() => setShowLabelPreview(false)}
        footer={[
          <PDFDownloadLink
            key="download"
            document={<MultiAssetLabelPDF assets={labelData} />}
            fileName={`tem_taisan_${Date.now()}.pdf`}
          >
            {({ loading }) => (
              <Button type="primary" loading={loading} disabled={labelData.length === 0}>
                Tải về PDF
              </Button>
            )}
          </PDFDownloadLink>,
          <Button key="close" onClick={() => setShowLabelPreview(false)}>Đóng</Button>
        ]}
        width="min(80vw, 1000px)"
        title={`Xem trước tem tài sản (${labelData.length})`}
      >
        <div style={{ height: 600, border: '1px solid #eee', borderRadius: 8, overflow: 'auto' }}>
          {labelData.length > 0 && (
            <PDFViewer width="100%" height={580} showToolbar={false} style={{ border: 'none' }}>
              <MultiAssetLabelPDF assets={labelData} />
            </PDFViewer>
          )}
        </div>
      </Modal>

      <AssetDetailModal
        visible={modalVisible}
        assetId={selectedAssetId}
        onClose={() => setModalVisible(false)}
      />

      <Modal
        open={createModalOpen}
        onCancel={() => { setCreateModalOpen(false); setCopyAsset(null); }}
        footer={null}
        title={copyAsset ? `Tạo mới từ bản sao: ${copyAsset.asset_code}` : 'Tạo mới tài sản'}
        destroyOnHidden
        width="min(80vw, 1000px)"
      >
        <AssetCreateForm
          companyList={companies}
          categoryList={categories}
          locationList={locations}
          assetList={assets}
          supplierList={suppliers}
          employeeList={employees}
          onSuccess={() => {
            setCreateModalOpen(false);
            setCopyAsset(null);
            fetchAssets(pagination.current, pagination.pageSize);
          }}
          onCancel={() => { setCreateModalOpen(false); setCopyAsset(null); }}
          onSupplierCreated={async () => {
            const res = await apiClient.getSuppliers();
            setSuppliers(Array.isArray(res) ? res : (res as any).data || []);
          }}
          onLocationCreated={async () => {
            const res = await apiClient.getLocations();
            setLocations(Array.isArray(res) ? res : (res as any).data || []);
          }}
          onCategoryCreated={async () => {
            const res = await apiClient.getAssetCategories();
            setCategories(Array.isArray(res) ? res : (res as any).data || []);
          }}
          onEmployeeCreated={async () => {
            const res = await apiClient.getEmployees();
            setEmployees(Array.isArray(res) ? res : (res as any).data || []);
          }}
          {...(copyAsset ? {
            initialValues: {
              ...copyAsset,
              id: undefined,
              asset_code: '',
              attachments: [],
              created_at: undefined,
              updated_at: undefined,
            }
          } : {})}
        />
      </Modal>

      <Modal
        open={editModalOpen}
        onCancel={() => { setEditModalOpen(false); setEditingAsset(null); }}
        footer={null}
        title="Chỉnh sửa tài sản"
        destroyOnHidden
        width="min(80vw, 1000px)"
      >
        {editingAsset && (
          <AssetEditForm
            assetId={editingAsset.id}
            companyList={companies}
            categoryList={categories}
            locationList={locations}
            supplierList={suppliers}
            employeeList={employees}
            onSuccess={() => {
              setEditModalOpen(false);
              setEditingAsset(null);
              fetchAssets(pagination.current, pagination.pageSize);
            }}
            onCancel={() => { setEditModalOpen(false); setEditingAsset(null); }}
            onSupplierCreated={async () => {
              const res = await apiClient.getSuppliers();
              setSuppliers(Array.isArray(res) ? res : (res as any).data || []);
            }}
            onLocationCreated={async () => {
              const res = await apiClient.getLocations();
              setLocations(Array.isArray(res) ? res : (res as any).data || []);
            }}
            onCategoryCreated={async () => {
              const res = await apiClient.getAssetCategories();
              setCategories(Array.isArray(res) ? res : (res as any).data || []);
            }}
            onEmployeeCreated={async () => {
              const res = await apiClient.getEmployees();
              setEmployees(Array.isArray(res) ? res : (res as any).data || []);
            }}
          />
        )}
      </Modal>

      <Modal
        open={qrModalVisible}
        onCancel={() => setQrModalVisible(false)}
        footer={null}
        title="QR Code tài sản"
        centered
      >
        {qrAsset && (
          <div style={{ textAlign: 'center' }}>
            <img
              src={qrCodeBase64Map[qrAsset.asset_code] || ''}
              alt="QR Code"
              style={{ width: 200, height: 200 }}
            />
            <div style={{ marginTop: 12, wordBreak: 'break-all', color: '#888' }}>
              {`${window.location.origin}/public/asset/${qrAsset.public_uuid}`}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AssetListPage;